// Inline-mark command primitives — bold / italic / strike / inline
// code / link. Each mutates the live DOM (the editor's
// contenteditable subtree) based on the current `Selection`.
//
// Implementation note: we don't use `document.execCommand`, even for
// `bold`/`italic` where browsers support it. Different engines emit
// different markup (Chromium → `<b>`, Gecko → `<strong>`; styled
// fragments may wrap selections in `<span style="font-weight:bold">`
// after pastes). Hand-rolling Selection/Range manipulation gives us
// canonical markup so the markdown serializer round-trips cleanly.

const INLINE_TAGS: ReadonlySet<string> = new Set([
  'STRONG',
  'EM',
  'S',
  'CODE',
  'A',
]);

/**
 * True iff every character in the current selection sits within an
 * ancestor with `tagName`. For a collapsed selection (caret) we
 * check whether the caret's parent chain includes the tag.
 */
export function isInlineMarkActive(
  root: HTMLElement,
  selection: Selection | null,
  tagName: string,
): boolean {
  if (selection === null || selection.rangeCount === 0) return false;
  const range = selection.getRangeAt(0);
  if (range.collapsed) {
    return hasAncestor(range.startContainer, tagName, root);
  }
  // Walk every text node inside the range. If any text node lacks
  // the requested ancestor, the mark is not uniformly active.
  const walker = document.createTreeWalker(
    range.commonAncestorContainer,
    NodeFilter.SHOW_TEXT,
  );
  let node: Node | null = walker.currentNode;
  while (node !== null) {
    if (range.intersectsNode(node)) {
      if (node.nodeValue !== null && node.nodeValue.length > 0) {
        if (!hasAncestor(node, tagName, root)) return false;
      }
    }
    node = walker.nextNode();
  }
  return true;
}

function hasAncestor(node: Node | null, tagName: string, root: HTMLElement): boolean {
  let cur: Node | null = node;
  while (cur !== null && cur !== root) {
    if (cur.nodeType === Node.ELEMENT_NODE) {
      if ((cur as HTMLElement).tagName === tagName) return true;
    }
    cur = cur.parentNode;
  }
  return false;
}

/**
 * Toggle an inline mark (`STRONG`, `EM`, `S`, `CODE`) over the
 * current selection. When the selection is uniformly marked → strip;
 * otherwise → wrap.
 *
 * Caret-only (collapsed) selections: insert empty mark tags and
 * place the caret inside so the next typed character lands marked.
 */
export function toggleInlineMark(
  root: HTMLElement,
  selection: Selection | null,
  tagName: 'STRONG' | 'EM' | 'S' | 'CODE',
): void {
  if (selection === null || selection.rangeCount === 0) return;
  const range = selection.getRangeAt(0);

  if (range.collapsed) {
    const doc = root.ownerDocument;
    // Caret sits inside an existing mark of this kind → toggle OFF
    // by exiting the mark. Park the caret just AFTER the closing
    // tag, with a zero-width sentinel so the browser doesn't snap
    // it back inside the mark on the next keystroke. Without this
    // branch, clicking the chip a second time would just nest a
    // fresh empty mark and the chip would stay highlighted.
    const ancestor = findAncestorMark(
      range.startContainer,
      tagName,
      root,
    );
    if (ancestor !== null) {
      const sentinel = doc.createTextNode('​');
      const parent = ancestor.parentNode;
      if (parent === null) return;
      parent.insertBefore(sentinel, ancestor.nextSibling);
      const placed = doc.createRange();
      placed.setStart(sentinel, sentinel.nodeValue?.length ?? 0);
      placed.collapse(true);
      selection.removeAllRanges();
      selection.addRange(placed);
      return;
    }
    // Otherwise: open a fresh empty mark so the next typed char
    // lands marked (sticky-format behavior).
    const mark = doc.createElement(tagName.toLowerCase());
    mark.appendChild(doc.createTextNode('​'));
    range.insertNode(mark);
    const placed = doc.createRange();
    placed.selectNodeContents(mark);
    placed.collapse(false);
    selection.removeAllRanges();
    selection.addRange(placed);
    return;
  }

  if (isInlineMarkActive(root, selection, tagName)) {
    unwrapInlineMark(root, selection, tagName);
    return;
  }
  wrapRangeInTag(root, range, tagName);
}

function findAncestorMark(
  node: Node | null,
  tagName: string,
  root: HTMLElement,
): HTMLElement | null {
  let cur: Node | null = node;
  while (cur !== null && cur !== root) {
    if (
      cur.nodeType === Node.ELEMENT_NODE &&
      (cur as HTMLElement).tagName === tagName
    ) {
      return cur as HTMLElement;
    }
    cur = cur.parentNode;
  }
  return null;
}

function wrapRangeInTag(
  root: HTMLElement,
  range: Range,
  tagName: 'STRONG' | 'EM' | 'S' | 'CODE',
): void {
  const doc = root.ownerDocument;
  const wrapper = doc.createElement(tagName.toLowerCase());
  // `extractContents()` removes the range's contents and returns a
  // DocumentFragment we can drop into the wrapper.
  wrapper.appendChild(range.extractContents());
  range.insertNode(wrapper);
  // After insertion the range collapses to the start of the
  // wrapper — re-select the wrapper's contents so chained commands
  // keep working.
  const fresh = doc.createRange();
  fresh.selectNodeContents(wrapper);
  const sel = doc.defaultView?.getSelection() ?? null;
  if (sel !== null) {
    sel.removeAllRanges();
    sel.addRange(fresh);
  }
  // Inline marks are not allowed inside `<pre>` — the visual is
  // monospaced anyway and the markdown wire format doesn't render
  // marks inside fences. We leave that to the block command path.
  void INLINE_TAGS;
}

function unwrapInlineMark(
  root: HTMLElement,
  selection: Selection,
  tagName: string,
): void {
  const range = selection.getRangeAt(0);
  // Two cases need handling:
  //   1. The tag is an ANCESTOR of the range (range is fully inside
  //      one matching element). Walk up from `commonAncestorContainer`.
  //   2. The tag sits as a DESCENDANT of the common ancestor and the
  //      range intersects it (e.g., selection spans `<p><strong>foo</strong></p>`
  //      where the range's common ancestor is the `<p>`). Walk down
  //      and unwrap every descendant whose nodes the range overlaps.
  let up: Node | null = range.commonAncestorContainer;
  while (up !== null && up !== root) {
    if (
      up.nodeType === Node.ELEMENT_NODE &&
      (up as HTMLElement).tagName === tagName
    ) {
      const el = up as HTMLElement;
      const parent = el.parentNode;
      if (parent === null) return;
      while (el.firstChild !== null) parent.insertBefore(el.firstChild, el);
      parent.removeChild(el);
      return;
    }
    up = up.parentNode;
  }
  // Walk-down case. Snapshot matches first since we mutate the DOM
  // while iterating.
  const ancestorEl =
    range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
      ? (range.commonAncestorContainer as HTMLElement)
      : range.commonAncestorContainer.parentElement;
  if (ancestorEl === null) return;
  const matches = Array.from(
    ancestorEl.querySelectorAll(tagName.toLowerCase()),
  ).filter((el) => range.intersectsNode(el));
  for (const el of matches) {
    const parent = el.parentNode;
    if (parent === null) continue;
    while (el.firstChild !== null) parent.insertBefore(el.firstChild, el);
    parent.removeChild(el);
  }
}

/**
 * Wrap the current selection in an `<a href="url">`. With a collapsed
 * caret inside an existing link we update that link's href in place;
 * with a collapsed caret elsewhere we insert a small `[label]` link
 * and put the caret on `label`.
 */
export function setLink(
  root: HTMLElement,
  selection: Selection | null,
  url: string,
  label?: string,
): void {
  if (selection === null || selection.rangeCount === 0) return;
  const range = selection.getRangeAt(0);
  const doc = root.ownerDocument;
  const hasLabel = label !== undefined && label.length > 0;
  // Park the caret just after `node` so typing continues outside the link.
  const caretAfter = (node: Node): void => {
    const r = doc.createRange();
    r.setStartAfter(node);
    r.collapse(true);
    selection.removeAllRanges();
    selection.addRange(r);
  };
  // Collapsed caret sitting inside an existing <a> → update href (and
  // text when a new label was supplied).
  if (range.collapsed) {
    const existing = findAncestorAnchor(root, range.startContainer);
    if (existing !== null) {
      existing.setAttribute('href', url);
      if (hasLabel && existing.textContent !== label) existing.textContent = label!;
      caretAfter(existing);
      return;
    }
  }
  const a = doc.createElement('a');
  a.setAttribute('href', url);
  if (range.collapsed) {
    // No selection — insert a fresh link. Use the supplied label, or
    // fall back to a placeholder so the user sees something to edit.
    a.textContent = hasLabel ? label! : 'link';
    range.insertNode(a);
    caretAfter(a);
    return;
  }
  // Selection covers content — if it sits entirely inside an existing
  // `<a>`, just update that anchor's href / text instead of nesting <a>s.
  const existing = findAncestorAnchor(root, range.startContainer);
  const existingEnd = findAncestorAnchor(root, range.endContainer);
  if (existing !== null && existing === existingEnd) {
    existing.setAttribute('href', url);
    if (hasLabel && existing.textContent !== label) existing.textContent = label!;
    caretAfter(existing);
    return;
  }
  // A new label replaces the selected text; otherwise keep the selection
  // as the link's visible text.
  if (hasLabel) {
    range.deleteContents();
    a.textContent = label!;
  } else {
    a.appendChild(range.extractContents());
  }
  range.insertNode(a);
  caretAfter(a);
}

/**
 * Unwrap the `<a>` containing the caret / selection. Caret stays at
 * the same character offset; the anchor element is removed and its
 * children promoted to the surrounding inline context. No-op when
 * no enclosing anchor exists.
 */
export function unwrapLink(
  root: HTMLElement,
  selection: Selection | null,
): boolean {
  if (selection === null || selection.rangeCount === 0) return false;
  const range = selection.getRangeAt(0);
  const a = findAncestorAnchor(root, range.startContainer);
  if (a === null) return false;
  const parent = a.parentNode;
  if (parent === null) return false;
  while (a.firstChild !== null) parent.insertBefore(a.firstChild, a);
  parent.removeChild(a);
  return true;
}

/**
 * Find the closest `<a>` ancestor of `node` within `root`, or null.
 */
export function findAncestorAnchor(
  root: HTMLElement,
  node: Node | null,
): HTMLAnchorElement | null {
  let cur: Node | null = node;
  while (cur !== null && cur !== root) {
    if (
      cur.nodeType === Node.ELEMENT_NODE &&
      (cur as HTMLElement).tagName === 'A'
    ) {
      return cur as HTMLAnchorElement;
    }
    cur = cur.parentNode;
  }
  return null;
}
